import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  InputAdornment,
  Snackbar,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Image as ImageIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Email,
  WhatsApp,
  Campaign,
  Analytics,
  Send,
  Settings,
  Cake,
  LocalOffer,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

// Import communication components
import EmailTemplateManager from '../components/communication/EmailTemplateManager';
import WhatsAppTemplateManager from '../components/communication/WhatsAppTemplateManager';
import CampaignManager from '../components/communication/CampaignManager';
import CommunicationConfig from '../components/communication/CommunicationConfig';
import CommunicationAnalytics from '../components/communication/CommunicationAnalytics';

// Import actions
import {
  getEmailTemplates,
  getWhatsAppTemplates,
  getCampaigns,
  getCommunicationStats,
  sendBirthdayWishes,
  sendOfferNotification
} from '../redux/actions/communicationActions';

const CommunicationManagement = () => {
  const dispatch = useDispatch();
  const { 
    loading, 
    error, 
    emailTemplates = [], 
    whatsappTemplates = [], 
    campaigns = [], 
    communicationStats = [] 
  } = useSelector((state) => state.communication || {});

  const [activeTab, setActiveTab] = useState(0);
  const [sendOfferDialog, setSendOfferDialog] = useState(false);
  const [sendBirthdayDialog, setSendBirthdayDialog] = useState(false);
  const [offerData, setOfferData] = useState({
    title: '',
    description: '',
    endDate: ''
  });
  const [offerImage, setOfferImage] = useState(null);
  const [offerImagePreview, setOfferImagePreview] = useState(null);
  const [birthdayData, setBirthdayData] = useState({
    discountPercentage: 10,
    limit: 10
  });
  const [birthdayImage, setBirthdayImage] = useState(null);
  const [birthdayImagePreview, setBirthdayImagePreview] = useState(null);
  const [sendMethods, setSendMethods] = useState(['email', 'whatsapp']);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load data only once when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load data sequentially to avoid overwhelming the server
        await dispatch(getEmailTemplates());
        await dispatch(getWhatsAppTemplates());
        await dispatch(getCampaigns());
        await dispatch(getCommunicationStats(
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          new Date().toISOString().split('T')[0]
        ));
      } catch (error) {
        console.error('Error loading communication data:', error);
        setSnackbar({ 
          open: true, 
          message: 'Failed to load communication data', 
          severity: 'error' 
        });
      }
    };

    loadInitialData();
  }, [dispatch]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRefresh = async () => {
    try {
      await dispatch(getEmailTemplates());
      await dispatch(getWhatsAppTemplates());
      await dispatch(getCampaigns());
      setSnackbar({ 
        open: true, 
        message: 'Data refreshed successfully', 
        severity: 'success' 
      });
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'Failed to refresh data', 
        severity: 'error' 
      });
    }
  };

  const handleSendBirthdayWishes = async () => {
    if (sendMethods.length === 0) {
      setSnackbar({ 
        open: true, 
        message: 'Please select at least one communication method', 
        severity: 'warning' 
      });
      return;
    }

    try {
      // Prepare image data if image is selected
      let imageData = null;
      if (birthdayImage) {
        imageData = {
          base64: birthdayImagePreview,
          mimeType: birthdayImage.type || 'image/jpeg'
        };
      }
      
      const result = await dispatch(sendBirthdayWishes(birthdayData.discountPercentage, sendMethods, imageData));
      if (result.success) {
        const data = result.data || {};
        const emailsSent = data.emailsSent || 0;
        const whatsappSent = data.whatsappSent || 0;
        
        // Check for failures
        const results = data.results || {};
        let hasFailures = false;
        let failureMessages = [];
        
        Object.keys(results).forEach(customerId => {
          const customerResults = results[customerId];
          if (customerResults.email && !customerResults.email.success) {
            hasFailures = true;
            failureMessages.push(`Email failed for customer ${customerId}`);
          }
          if (customerResults.whatsapp && !customerResults.whatsapp.success) {
            hasFailures = true;
            failureMessages.push(`WhatsApp failed for customer ${customerId}`);
          }
        });

        if (hasFailures && (emailsSent === 0 && whatsappSent === 0)) {
          setSnackbar({ 
            open: true, 
            message: `Failed to send birthday wishes: ${failureMessages.join(', ')}`, 
            severity: 'error' 
          });
        } else if (hasFailures) {
          setSnackbar({ 
            open: true, 
            message: `Birthday wishes sent partially. Emails: ${emailsSent}, WhatsApp: ${whatsappSent}. Some failures occurred.`, 
            severity: 'warning' 
          });
        } else {
          setSnackbar({ 
            open: true, 
            message: `Birthday wishes sent successfully! Emails: ${emailsSent}, WhatsApp: ${whatsappSent}`, 
            severity: 'success' 
          });
        }
        setSendBirthdayDialog(false);
        setBirthdayData({ discountPercentage: 10, limit: 10 });
        setBirthdayImage(null);
        setBirthdayImagePreview(null);
        setSendMethods(['email', 'whatsapp']);
      } else {
        setSnackbar({ 
          open: true, 
          message: result.message || 'Failed to send birthday wishes', 
          severity: 'error' 
        });
      }
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'Error sending birthday wishes', 
        severity: 'error' 
      });
    }
  };

  const handleSendOffer = async () => {
    if (sendMethods.length === 0) {
      setSnackbar({ 
        open: true, 
        message: 'Please select at least one communication method', 
        severity: 'warning' 
      });
      return;
    }

    try {
      // Prepare image data if image is selected
      let imageData = null;
      if (offerImage) {
        imageData = {
          base64: offerImagePreview,
          mimeType: offerImage.type || 'image/jpeg'
        };
      }
      
      const result = await dispatch(sendOfferNotification(offerData, {}, sendMethods, imageData));
      if (result.success) {
        const data = result.data || {};
        const emailsSent = data.emailsSent || 0;
        const whatsappSent = data.whatsappSent || 0;
        
        // Check for failures
        const results = data.results || {};
        let hasFailures = false;
        let failureMessages = [];
        
        Object.keys(results).forEach(customerId => {
          const customerResults = results[customerId];
          if (customerResults.email && !customerResults.email.success) {
            hasFailures = true;
            failureMessages.push(`Email failed for customer ${customerId}`);
          }
          if (customerResults.whatsapp && !customerResults.whatsapp.success) {
            hasFailures = true;
            failureMessages.push(`WhatsApp failed for customer ${customerId}`);
          }
        });

        if (hasFailures && (emailsSent === 0 && whatsappSent === 0)) {
          setSnackbar({ 
            open: true, 
            message: `Failed to send offer notification: ${failureMessages.join(', ')}`, 
            severity: 'error' 
          });
        } else if (hasFailures) {
          setSnackbar({ 
            open: true, 
            message: `Offer notification sent partially. Emails: ${emailsSent}, WhatsApp: ${whatsappSent}. Some failures occurred.`, 
            severity: 'warning' 
          });
        } else {
          setSnackbar({ 
            open: true, 
            message: `Offer notification sent successfully! Emails: ${emailsSent}, WhatsApp: ${whatsappSent}`, 
            severity: 'success' 
          });
        }
        setSendOfferDialog(false);
        setOfferData({ title: '', description: '', endDate: '' });
        setOfferImage(null);
        setOfferImagePreview(null);
        setSendMethods(['email', 'whatsapp']);
      } else {
        setSnackbar({ 
          open: true, 
          message: result.message || 'Failed to send offer notification', 
          severity: 'error' 
        });
      }
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'Error sending offer notification', 
        severity: 'error' 
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const activeCampaigns = campaigns.filter(c => c.status === 'running').length;
  const completedCampaigns = campaigns.filter(c => c.status === 'completed').length;

  if (loading && emailTemplates.length === 0 && whatsappTemplates.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ 
        mb: 4,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Communication Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage email templates, WhatsApp templates, campaigns, and customer communications
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={handleRefresh} 
              disabled={loading}
              sx={{ 
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Settings />}
            onClick={() => setActiveTab(3)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
              }
            }}
          >
            Configuration
          </Button>
          <Button
            variant="outlined"
            startIcon={<Cake />}
            onClick={() => setSendBirthdayDialog(true)}
            disabled={loading}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              px: 3
            }}
          >
            Send Birthday Wishes
          </Button>
          <Button
            variant="contained"
            startIcon={<LocalOffer />}
            onClick={() => setSendOfferDialog(true)}
            disabled={loading}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653d91 100%)',
              }
            }}
          >
            Send Offer
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {}}
            >
              <ErrorIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            boxShadow: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="rgba(255,255,255,0.8)" gutterBottom variant="body2">
                    Email Templates
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {emailTemplates.length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {emailTemplates.filter(t => t.is_active).length} active
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 64, 
                  height: 64 
                }}>
                  <Email sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            boxShadow: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="rgba(255,255,255,0.8)" gutterBottom variant="body2">
                    WhatsApp Templates
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {whatsappTemplates.length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {whatsappTemplates.filter(t => t.is_active).length} active
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 64, 
                  height: 64 
                }}>
                  <WhatsApp sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            boxShadow: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="rgba(255,255,255,0.8)" gutterBottom variant="body2">
                    Active Campaigns
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {activeCampaigns}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {completedCampaigns} completed
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 64, 
                  height: 64 
                }}>
                  <Campaign sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            boxShadow: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="rgba(255,255,255,0.8)" gutterBottom variant="body2">
                    Total Campaigns
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {campaigns.length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    All time
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 64, 
                  height: 64 
                }}>
                  <Analytics sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Card sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'background.paper',
          px: 1
        }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 64,
                fontSize: '0.95rem',
                gap: 1,
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 'bold',
                  bgcolor: 'primary.light',
                  borderRadius: '8px 8px 0 0'
                },
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              },
              '& .MuiTabs-indicator': {
                display: 'none'
              }
            }}
          >
            <Tab 
              icon={<Email />} 
              iconPosition="start" 
              label="Email Templates"
              sx={{ gap: 1 }}
            />
            <Tab 
              icon={<WhatsApp />} 
              iconPosition="start" 
              label="WhatsApp Templates"
              sx={{ gap: 1 }}
            />
            <Tab 
              icon={<Campaign />} 
              iconPosition="start" 
              label="Campaigns"
              sx={{ gap: 1 }}
            />
            <Tab 
              icon={<Settings />} 
              iconPosition="start" 
              label="Configuration"
              sx={{ 
                gap: 1,
                color: activeTab === 3 ? 'primary.main' : 'text.primary',
                '&.Mui-selected': {
                  color: 'primary.main',
                  bgcolor: 'primary.light'
                }
              }}
            />
            <Tab 
              icon={<Analytics />} 
              iconPosition="start" 
              label="Analytics"
              sx={{ gap: 1 }}
            />
          </Tabs>
        </Box>
        <Box sx={{ p: 3, minHeight: 400, bgcolor: 'background.paper' }}>
          {activeTab === 0 && <EmailTemplateManager />}
          {activeTab === 1 && <WhatsAppTemplateManager />}
          {activeTab === 2 && <CampaignManager />}
          {activeTab === 3 && <CommunicationConfig />}
          {activeTab === 4 && <CommunicationAnalytics />}
        </Box>
      </Card>

      {/* Send Offer Dialog */}
      <Dialog 
        open={sendOfferDialog} 
        onClose={() => setSendOfferDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <LocalOffer />
          <Typography variant="h6" fontWeight="bold">
            Send Offer Notification
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Offer Title"
              variant="outlined"
              value={offerData.title}
              onChange={(e) => setOfferData({ ...offerData, title: e.target.value })}
              placeholder="e.g., Diwali Special Offer"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalOffer color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Offer Description"
              multiline
              rows={4}
              variant="outlined"
              value={offerData.description}
              onChange={(e) => setOfferData({ ...offerData, description: e.target.value })}
              placeholder="Describe your offer details..."
            />
            <TextField
              fullWidth
              label="End Date"
              type="date"
              variant="outlined"
              value={offerData.endDate}
              onChange={(e) => setOfferData({ ...offerData, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Campaign color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload Image (Optional - for WhatsApp):
            </Typography>
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="offer-image-upload"
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setOfferImage(file);
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setOfferImagePreview(reader.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <label htmlFor="offer-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImageIcon />}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {offerImage ? 'Change Image' : 'Upload Image'}
                </Button>
              </label>
              {offerImagePreview && (
                <Box sx={{ position: 'relative', mt: 2 }}>
                  <img
                    src={offerImagePreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      setOfferImage(null);
                      setOfferImagePreview(null);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select communication methods:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card 
                sx={{ 
                  border: sendMethods.includes('email') ? '2px solid' : '1px solid',
                  borderColor: sendMethods.includes('email') ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                }}
                onClick={() => {
                  if (sendMethods.includes('email')) {
                    setSendMethods(sendMethods.filter(m => m !== 'email'));
                  } else {
                    setSendMethods([...sendMethods, 'email']);
                  }
                }}
              >
                <CardContent>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendMethods.includes('email')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSendMethods([...sendMethods, 'email']);
                          } else {
                            setSendMethods(sendMethods.filter(m => m !== 'email'));
                          }
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email color="primary" />
                        <Typography variant="body1" fontWeight="medium">
                          Email
                        </Typography>
                      </Box>
                    }
                  />
                </CardContent>
              </Card>

              <Card 
                sx={{ 
                  border: sendMethods.includes('whatsapp') ? '2px solid' : '1px solid',
                  borderColor: sendMethods.includes('whatsapp') ? 'success.main' : 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'success.main', bgcolor: 'action.hover' }
                }}
                onClick={() => {
                  if (sendMethods.includes('whatsapp')) {
                    setSendMethods(sendMethods.filter(m => m !== 'whatsapp'));
                  } else {
                    setSendMethods([...sendMethods, 'whatsapp']);
                  }
                }}
              >
                <CardContent>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendMethods.includes('whatsapp')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSendMethods([...sendMethods, 'whatsapp']);
                          } else {
                            setSendMethods(sendMethods.filter(m => m !== 'whatsapp'));
                          }
                        }}
                        color="success"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WhatsApp sx={{ color: 'success.main' }} />
                        <Typography variant="body1" fontWeight="medium">
                          WhatsApp
                        </Typography>
                      </Box>
                    }
                  />
                </CardContent>
              </Card>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={() => {
              setSendOfferDialog(false);
              setOfferData({ title: '', description: '', endDate: '' });
              setOfferImage(null);
              setOfferImagePreview(null);
              setSendMethods(['email', 'whatsapp']);
            }}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendOffer} 
            variant="contained"
            disabled={!offerData.title || !offerData.description || sendMethods.length === 0}
            startIcon={<Send />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              px: 3
            }}
          >
            Send Offer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Birthday Wishes Dialog */}
      <Dialog 
        open={sendBirthdayDialog} 
        onClose={() => setSendBirthdayDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'warning.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Cake />
          <Typography variant="h6" fontWeight="bold">
            Send Birthday Wishes
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Discount Percentage"
              type="number"
              variant="outlined"
              value={birthdayData.discountPercentage}
              onChange={(e) => setBirthdayData({ ...birthdayData, discountPercentage: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">%</InputAdornment>
                ),
              }}
              helperText="Discount percentage to offer on birthday"
            />
            <TextField
              fullWidth
              label="Customer Limit"
              type="number"
              variant="outlined"
              value={birthdayData.limit}
              onChange={(e) => setBirthdayData({ ...birthdayData, limit: e.target.value })}
              helperText="Maximum number of customers to send wishes to"
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload Image (Optional - for WhatsApp):
            </Typography>
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="birthday-image-upload"
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setBirthdayImage(file);
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setBirthdayImagePreview(reader.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <label htmlFor="birthday-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImageIcon />}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {birthdayImage ? 'Change Image' : 'Upload Image'}
                </Button>
              </label>
              {birthdayImagePreview && (
                <Box sx={{ position: 'relative', mt: 2 }}>
                  <img
                    src={birthdayImagePreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      setBirthdayImage(null);
                      setBirthdayImagePreview(null);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select communication methods:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card 
                sx={{ 
                  border: sendMethods.includes('email') ? '2px solid' : '1px solid',
                  borderColor: sendMethods.includes('email') ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                }}
                onClick={() => {
                  if (sendMethods.includes('email')) {
                    setSendMethods(sendMethods.filter(m => m !== 'email'));
                  } else {
                    setSendMethods([...sendMethods, 'email']);
                  }
                }}
              >
                <CardContent>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendMethods.includes('email')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSendMethods([...sendMethods, 'email']);
                          } else {
                            setSendMethods(sendMethods.filter(m => m !== 'email'));
                          }
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email color="primary" />
                        <Typography variant="body1" fontWeight="medium">
                          Email
                        </Typography>
                      </Box>
                    }
                  />
                </CardContent>
              </Card>

              <Card 
                sx={{ 
                  border: sendMethods.includes('whatsapp') ? '2px solid' : '1px solid',
                  borderColor: sendMethods.includes('whatsapp') ? 'success.main' : 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'success.main', bgcolor: 'action.hover' }
                }}
                onClick={() => {
                  if (sendMethods.includes('whatsapp')) {
                    setSendMethods(sendMethods.filter(m => m !== 'whatsapp'));
                  } else {
                    setSendMethods([...sendMethods, 'whatsapp']);
                  }
                }}
              >
                <CardContent>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendMethods.includes('whatsapp')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSendMethods([...sendMethods, 'whatsapp']);
                          } else {
                            setSendMethods(sendMethods.filter(m => m !== 'whatsapp'));
                          }
                        }}
                        color="success"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WhatsApp sx={{ color: 'success.main' }} />
                        <Typography variant="body1" fontWeight="medium">
                          WhatsApp
                        </Typography>
                      </Box>
                    }
                  />
                </CardContent>
              </Card>
            </Box>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              This will send birthday wishes to customers whose birthday is today. 
              The system will automatically select customers and send via the selected methods.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={() => {
              setSendBirthdayDialog(false);
              setBirthdayData({ discountPercentage: 10, limit: 10 });
              setBirthdayImage(null);
              setBirthdayImagePreview(null);
              setSendMethods(['email', 'whatsapp']);
            }}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendBirthdayWishes} 
            variant="contained"
            color="warning"
            disabled={sendMethods.length === 0}
            startIcon={<Send />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              px: 3
            }}
          >
            Send Birthday Wishes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          icon={snackbar.severity === 'success' ? <CheckCircle /> : <ErrorIcon />}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunicationManagement;
